import type {
  CalendarItem,
  File,
  Folder,
  Page,
  ReadingList,
  Resource,
} from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import type { Store } from '@warp-drive/core';

export type PageContext =
  | {
      type: 'cohort';
      programId: string;
      cohortId: string;
    }
  | {
      type: 'module';
      programId: string;
      cohortId: string;
      moduleId: string;
    };

export class PageManager {
  constructor(
    private readonly store: Store,
    public readonly page: Page,
    public readonly context: PageContext
  ) {}

  async ensureReadingList() {
    const readingLists = await this.page.readinglists;
    if (!readingLists.length) {
      const readingList = this.store.createRecord<ReadingList>(
        DataTypeContent.ReadingList,
        {
          title: 'Playlist',
        }
      );
      readingLists.push(readingList);
    }
    return readingLists[0]!;
  }

  async addResourceToPlaylist(resource: Resource) {
    const readingList = await this.ensureReadingList();
    const resources = await readingList.resources;
    if (resources.some((r) => r.id === resource.id)) {
      return;
    }
    resources.push(resource);
  }

  async findResourceInPlaylist(resourceId: string) {
    const readingList = await this.ensureReadingList();
    const resources = await readingList.resources;
    return resources.find((r) => r.id === resourceId) ?? null;
  }

  async addCalendarItem(calendarItem: CalendarItem) {
    const calendarItems = await this.page.calendarItems;
    if (calendarItems.some((c) => c.id === calendarItem.id)) {
      return;
    }
    calendarItems.push(calendarItem);
  }

  async findCalendarItems(calendarItemIds: string[]) {
    const allCalendarItems = await this.page.calendarItems;
    const calendarItems =
      calendarItemIds.map((id) => allCalendarItems.find((c) => c.id === id)) ??
      [];
    const foundCalendarItems = calendarItems.filter(
      (c): c is CalendarItem => c !== undefined
    );

    // Sort by starts date
    foundCalendarItems.sort((a, b) => {
      return new Date(a.starts).getTime() - new Date(b.starts).getTime();
    });

    return foundCalendarItems;
  }

  async addFiles(files: File[]) {
    const allFiles = await this.page.files;
    for (const file of files) {
      if (allFiles.some((f) => f.id === file.id)) {
        continue;
      }
      allFiles.push(file);
    }
  }

  async addFolders(folders: Folder[]) {
    const allFolders = await this.page.folders;
    for (const folder of folders) {
      if (allFolders.some((f) => f.id === folder.id)) {
        continue;
      }
      allFolders.push(folder);
    }
  }

  async findFiles(fileIds: string[]) {
    const allFiles = await this.page.files;
    const files = fileIds.map((id) => allFiles.find((f) => f.id === id)) ?? [];
    const foundFiles = files.filter((f): f is File => f !== undefined);
    return foundFiles;
  }

  async findFolders(folderIds: string[]) {
    const allFolders = await this.page.folders;
    const folders =
      folderIds.map((id) => allFolders.find((f) => f.id === id)) ?? [];
    const foundFolders = folders.filter((f): f is Folder => f !== undefined);
    return foundFolders;
  }
}
